import React, { PureComponent } from 'react'
import Layout from '../../components/Layout'
import { Container, Table, Header } from 'semantic-ui-react'
import { Link } from 'react-router-dom'

import { PrismAsyncLight as SyntaxHighlighter } from "react-syntax-highlighter";
import { tomorrow as SyntaxStyle } from 'react-syntax-highlighter/dist/cjs/styles/prism';
import { productNameWords } from '../../services/words';

export default class FormatConversion extends PureComponent {

  constructor(props) {
    super(props)
    this.reportConversionRef = React.createRef()
  }

  componentDidMount() {
    if (window.location.hash === '#report-conversion') {
      const scrollToElement = () => {
        if (this.reportConversionRef.current) {
          this.reportConversionRef.current.scrollIntoView({ behavior: 'smooth' })
        } else {
          setTimeout(scrollToElement, 500)
        }
      }
      setTimeout(scrollToElement, 500)
    }
  }

  render() {

    return (
      <Layout header='Format conversion'>
        <Container>
          <Header as='h3'>Why format conversion?</Header>
          <p>
            Whilst the Everfox CDR engine supports a wide variety of file formats, there are some files that it cannot transform, for example the old style Office 97 documents.
            In order to extend support to these kinds of files the APIs have a facility to convert some additional file types to ones that can be transformed.
          </p>

          <Header as='h3'>How to enable format conversion</Header>
          <p>
            Format conversion is enabled by specifying the conversion.from type in the options object for the
            <Link to='/api/async/definition'> Async </Link>
            and
            <Link to='/api/s3/definition'> S3 </Link>
            APIs, or the X-Options header for the
            <Link to='/api/instant/definition'> Instant </Link>
            API, as well as the primary mime-type to be converted to.
          </p>
          <SyntaxHighlighter language='json' style={SyntaxStyle}>
            {
              '{\n' +
              '  "mime-type": "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",\n' +
              '  "options": {\n' +
              '    "conversion": {\n' +
              '      "from": { "mime-type": "application/vnd.ms-excel" }\n' +
              '    }\n' +
              '  }\n' +
              '}'
            }
          </SyntaxHighlighter>

          <p>
            If the conversion is supported the data is converted from the conversion.from type into the primary mime-type, and then the converted data is transformed and returned.
          </p>

          <Header as='h3'>Automatic conversion</Header>
          <p>
            If the primary mime-type is set to application/octet-stream then the mime-type of the output file will be automatically
            selected based on the conversion.from type and the current best quality conversion for that type. As the range of file
            formats supported by the Everfox CDR engine is expanded and new conversion tools are added, the automatic conversion
            mappings will change to ensure the highest quality output file is generated.
            The following example converts a Word 97 document into the best format for transformation (currently .docx).
          </p>
          <SyntaxHighlighter language='json' style={SyntaxStyle}>
            {
              '{\n' +
              '  "mime-type": "application/octet-stream",\n' +
              '  "options": {\n' +
              '    "conversion": {\n' +
              '      "from": { "mime-type": "application/msword" }\n' +
              '    }\n' +
              '  }\n' +
              '}'
            }
          </SyntaxHighlighter>

          <p>
            With the same setup, passing a jpeg with conversion.from set to "image/jpeg" will cause the image to be transformed
            and returned as a jpeg file, the conversion will be skipped as image/jpeg is supported.
          </p>
          <SyntaxHighlighter language='json' style={SyntaxStyle}>
            {
              '{\n' +
              '  "mime-type": "application/octet-stream",\n' +
              '  "options": {\n' +
              '    "conversion": {\n' +
              '      "from": { "mime-type": "image/jpeg" }\n' +
              '    }\n' +
              '  }\n' +
              '}'
            }
          </SyntaxHighlighter>
          <br />

          <p>
            Note using automatic conversion with the Instant API will still require the primary mime-type to be specified in the Accept header, although this header can contain a list of possible types as well as */* to allow all types.
          </p>
          <Header as='h3'>Valid conversions</Header>
          <Table celled selectable>
            <Table.Header>
              <Table.Row textAlign='center'>
                <Table.HeaderCell colSpan='3'>Original file</Table.HeaderCell>
                <Table.HeaderCell rowSpan='2'>Default conversion tool</Table.HeaderCell>
                <Table.HeaderCell colSpan='3'>Output file</Table.HeaderCell>
              </Table.Row>
              <Table.Row textAlign='center'>
                <Table.HeaderCell>Format</Table.HeaderCell>
                <Table.HeaderCell>Extension</Table.HeaderCell>
                <Table.HeaderCell>Mime type</Table.HeaderCell>
                <Table.HeaderCell>Format</Table.HeaderCell>
                <Table.HeaderCell>Extension</Table.HeaderCell>
                <Table.HeaderCell>Mime type</Table.HeaderCell>
              </Table.Row>
            </Table.Header>

            <Table.Body>
              <Table.Row>
                <Table.Cell>MS Word 97</Table.Cell>
                <Table.Cell>.doc</Table.Cell>
                <Table.Cell>application/msword</Table.Cell>
                <Table.Cell>Libre Office*</Table.Cell>
                <Table.Cell>Office X Word</Table.Cell>
                <Table.Cell>.docx</Table.Cell>
                <Table.Cell>application/vnd.openxmlformats-officedocument.wordprocessingml.document</Table.Cell>
              </Table.Row>
              <Table.Row>
                <Table.Cell>MS PowerPoint 97</Table.Cell>
                <Table.Cell>.ppt</Table.Cell>
                <Table.Cell>application/vnd.ms-powerpoint</Table.Cell>
                <Table.Cell>Libre Office*</Table.Cell>
                <Table.Cell>Office X PowerPoint</Table.Cell>
                <Table.Cell>.pptx</Table.Cell>
                <Table.Cell>application/vnd.openxmlformats-officedocument.presentationml.presentation</Table.Cell>
              </Table.Row>
              <Table.Row>
                <Table.Cell>MS Excel 97</Table.Cell>
                <Table.Cell>.xls</Table.Cell>
                <Table.Cell>application/vnd.ms-excel</Table.Cell>
                <Table.Cell>Libre Office*</Table.Cell>
                <Table.Cell>Office X Excel</Table.Cell>
                <Table.Cell>.xlsx</Table.Cell>
                <Table.Cell>application/vnd.openxmlformats-officedocument.spreadsheetml.sheet</Table.Cell>
              </Table.Row>
              <Table.Row>
                <Table.Cell>MS Excel 97 (with macros)</Table.Cell>
                <Table.Cell>.xls</Table.Cell>
                <Table.Cell>application/vnd.ms-excel</Table.Cell>
                <Table.Cell>Libre Office*</Table.Cell>
                <Table.Cell>Office X Excel</Table.Cell>
                <Table.Cell>.xlsm</Table.Cell>
                <Table.Cell>application/vnd.ms-excel.sheet.macroEnabled.12</Table.Cell>
              </Table.Row>
              <Table.Row>
                <Table.Cell>MS Excel 2007 Binary</Table.Cell>
                <Table.Cell>.xlsb</Table.Cell>
                <Table.Cell>application/vnd.ms-excel.sheet.binary.macroenabled.12</Table.Cell>
                <Table.Cell>Libre Office*</Table.Cell>
                <Table.Cell>Office X Excel</Table.Cell>
                <Table.Cell>.xlsx</Table.Cell>
                <Table.Cell>application/vnd.openxmlformats-officedocument.spreadsheetml.sheet</Table.Cell>
              </Table.Row>
              <Table.Row>
                <Table.Cell>Open Office Writer</Table.Cell>
                <Table.Cell>.odt</Table.Cell>
                <Table.Cell>application/vnd.oasis.opendocument.text</Table.Cell>
                <Table.Cell>Libre Office*</Table.Cell>
                <Table.Cell>Office X Word</Table.Cell>
                <Table.Cell>.docx</Table.Cell>
                <Table.Cell>application/vnd.openxmlformats-officedocument.wordprocessingml.document</Table.Cell>
              </Table.Row>
              <Table.Row>
                <Table.Cell>Open Office Impress</Table.Cell>
                <Table.Cell>.odp</Table.Cell>
                <Table.Cell>application/vnd.oasis.opendocument.presentation</Table.Cell>
                <Table.Cell>Libre Office*</Table.Cell>
                <Table.Cell>Office X PowerPoint</Table.Cell>
                <Table.Cell>.pptx</Table.Cell>
                <Table.Cell>application/vnd.openxmlformats-officedocument.presentationml.presentation</Table.Cell>
              </Table.Row>
              <Table.Row>
                <Table.Cell>Open Office Calc</Table.Cell>
                <Table.Cell>.ods</Table.Cell>
                <Table.Cell>application/vnd.oasis.opendocument.spreadsheet</Table.Cell>
                <Table.Cell>Libre Office*</Table.Cell>
                <Table.Cell>Office X Excel</Table.Cell>
                <Table.Cell>.xlsx</Table.Cell>
                <Table.Cell>application/vnd.openxmlformats-officedocument.spreadsheetml.sheet</Table.Cell>
              </Table.Row>
              <Table.Row>
                <Table.Cell>CSV</Table.Cell>
                <Table.Cell>.csv</Table.Cell>
                <Table.Cell>text/csv</Table.Cell>
                <Table.Cell>Libre Office*</Table.Cell>
                <Table.Cell>CSV</Table.Cell>
                <Table.Cell>.xlsx</Table.Cell>
                <Table.Cell>application/vnd.openxmlformats-officedocument.spreadsheetml.sheet</Table.Cell>
              </Table.Row>
            </Table.Body>
          </Table>
          <p>
            * For office document conversions processed by Libre Office, there may be some loss of fidelity in the conversion process, see the
            <a rel="noopener noreferrer" target="_blank" href='https://help.libreoffice.org/6.3/en-US/text/shared/guide/ms_import_export_limitations.html?&DbPAR=WRITER&System=WIN'> Libre Office documentation </a>
            for details.
          </p>
          <Header as='h3'>Additional changes that are similar to conversions</Header>
          <Table celled selectable>
            <Table.Header>
              <Table.Row textAlign='center'>
                <Table.HeaderCell>Original file types</Table.HeaderCell>
                <Table.HeaderCell>Output file types</Table.HeaderCell>
              </Table.Row>
            </Table.Header>

            <Table.Body>
              <Table.Row>
                <Table.Cell>Macro-enabled Microsoft Office formats</Table.Cell>
                <Table.Cell>The equivalent non-macro enabled format</Table.Cell>
              </Table.Row>
              <Table.Row>
                <Table.Cell>Images with an incorrect image extension, or submitted with a different mime type</Table.Cell>
                <Table.Cell>The image data is converted to the type of the extension or mime type given</Table.Cell>
              </Table.Row>
            </Table.Body>
          </Table>

          <Header as='h3'>Conversion method</Header>
          <p>
            New conversion tools that enable more types of conversions will be added in the future. You can control which tools can be used to process your files through the conversion methods field.
            This will allow you to set which tools to enable and disable.
            If the methods field is omitted then the default tool set is used, if you disable "default" then the conversion process will only use tools you have explicitly enabled.
          </p>
          <p>
            The following example shows the default tools being disabled and Libre Office explicitly enabled ensuring that only Libre Office will ever be used for the conversion.
          </p>
          <SyntaxHighlighter language='json' style={SyntaxStyle}>
            {
              '{\n' +
              '  "mime-type": "application/octet-stream",\n' +
              '  "options": {\n' +
              '    "conversion": { "from": { "mime-type": "application/vnd.oasis.opendocument.spreadsheet" }},\n' +
              '    "method": { "disabled": "default", "enabled": "libreoffice" }\n' +
              '  }\n' +
              '}'
            }
          </SyntaxHighlighter>
          <p>
            Currently, all conversions happen within the {productNameWords} service, but in the future we may offer
            conversions that integrate with external third party services.
            However, no such conversion will ever be included in the set of default conversion tools.
            This means you will always have explicit control over which services we pass your data to.
          </p>
          <div id="report-conversion" ref={this.reportConversionRef}></div>
          <Header as='h3'>Report</Header>
          <p>
            The way the report usually is returned has some size restrictions.
            By enabling this conversion the report is returned instead of the normal CDR result.
            This allows for larger report sizes. It is specifically useful in scenarios such as text extraction, and files which contain a lot of items.
            You can tell that a normal report has been compressed by the "report__compressed" property, the <Link to='/documentation/report#report-compression'>report documentation</Link> has more information.
            Starting CDR again with the report conversion enabled would allow you to see the uncompressed report.

          </p>

          <SyntaxHighlighter language='json' style={SyntaxStyle}>
            {
              '{\n' +
              '  "mime-type": "application/octet-stream",\n' +
              '  "options": {\n' +
              '    {"conversion": { "methods": { "enabled": ["report"] }}}\n' +
              '  }\n' +
              '}'
            }
          </SyntaxHighlighter>

          <Header as='h3'>Text extraction</Header>
          <p>
            The "text-extraction" conversion option instructs Everfox CDR to generate text versions of supported documents and add them into the report.
            The extracted text will be in JSON format and is designed to capture all the text present in the document, it is not meant to be human friendly.
            This option can be used in conjunction with the "report" conversion option, which will return the report instead of the transformed file.
            This can be useful for large text extractions or when the file output is not required.
          </p>
          <Table celled selectable>
            <Table.Header>
              <Table.Row textAlign='center'>
                <Table.HeaderCell colSpan='3'>Supported files</Table.HeaderCell>
              </Table.Row>
              <Table.Row textAlign='center'>
                <Table.HeaderCell>Format</Table.HeaderCell>
                <Table.HeaderCell>Extension</Table.HeaderCell>
                <Table.HeaderCell>Mime type</Table.HeaderCell>
              </Table.Row>
            </Table.Header>
            <Table.Body>
              <Table.Row>
                <Table.Cell>MS Word 97</Table.Cell>
                <Table.Cell>.doc</Table.Cell>
                <Table.Cell>application/msword</Table.Cell>
              </Table.Row>
              <Table.Row>
                <Table.Cell>Word Macro-Enabled Document</Table.Cell>
                <Table.Cell>.docm</Table.Cell>
                <Table.Cell>application/vnd.ms-word.document.macroEnabled.12</Table.Cell>
              </Table.Row>
              <Table.Row>
                <Table.Cell></Table.Cell>
                <Table.Cell>.docx</Table.Cell>
                <Table.Cell>application/vnd.openxmlformats-officedocument.wordprocessingml.document</Table.Cell>
              </Table.Row>
              <Table.Row>
                <Table.Cell></Table.Cell>
                <Table.Cell>.dotm</Table.Cell>
                <Table.Cell>application/vnd.ms-word.template.macroEnabled.12</Table.Cell>
              </Table.Row>
              <Table.Row>
                <Table.Cell></Table.Cell>
                <Table.Cell>.dotx</Table.Cell>
                <Table.Cell>application/vnd.openxmlformats-officedocument.wordprocessingml.template</Table.Cell>
              </Table.Row>
              <Table.Row>
                <Table.Cell>Open Office Impress</Table.Cell>
                <Table.Cell>.odp</Table.Cell>
                <Table.Cell>application/vnd.oasis.opendocument.presentation</Table.Cell>
              </Table.Row>
              <Table.Row>
                <Table.Cell>Open Office Calc</Table.Cell>
                <Table.Cell>.ods</Table.Cell>
                <Table.Cell>application/vnd.oasis.opendocument.spreadsheet</Table.Cell>
              </Table.Row>
              <Table.Row>
                <Table.Cell>Open Office Writer</Table.Cell>
                <Table.Cell>.odt</Table.Cell>
                <Table.Cell>application/vnd.oasis.opendocument.text</Table.Cell>
              </Table.Row>
              <Table.Row>
                <Table.Cell> </Table.Cell>
                <Table.Cell>.potm</Table.Cell>
                <Table.Cell>application/vnd.ms-powerpoint.template.macroEnabled.12</Table.Cell>
              </Table.Row>
              <Table.Row>
                <Table.Cell> </Table.Cell>
                <Table.Cell>.potx</Table.Cell>
                <Table.Cell>application/vnd.openxmlformats-officedocument.presentationml.template</Table.Cell>
              </Table.Row>
              <Table.Row>
                <Table.Cell> </Table.Cell>
                <Table.Cell>.ppam</Table.Cell>
                <Table.Cell>application/vnd.ms-powerpoint.addin.macroEnabled.12</Table.Cell>
              </Table.Row>
              <Table.Row>
                <Table.Cell> </Table.Cell>
                <Table.Cell>.ppsm</Table.Cell>
                <Table.Cell>application/vnd.ms-powerpoint.slideshow.macroEnabled.12</Table.Cell>
              </Table.Row>
              <Table.Row>
                <Table.Cell> </Table.Cell>
                <Table.Cell>.ppsx</Table.Cell>
                <Table.Cell>application/vnd.openxmlformats-officedocument.presentationml.slideshow</Table.Cell>
              </Table.Row>
              <Table.Row>
                <Table.Cell>MS PowerPoint 97</Table.Cell>
                <Table.Cell>.ppt</Table.Cell>
                <Table.Cell>application/vnd.ms-powerpoint</Table.Cell>
              </Table.Row>
              <Table.Row>
                <Table.Cell> </Table.Cell>
                <Table.Cell>.pptm</Table.Cell>
                <Table.Cell>application/vnd.ms-powerpoint.presentation.macroEnabled.12</Table.Cell>
              </Table.Row>
              <Table.Row>
                <Table.Cell> </Table.Cell>
                <Table.Cell>.pptx</Table.Cell>
                <Table.Cell>application/vnd.openxmlformats-officedocument.presentationml.presentation</Table.Cell>
              </Table.Row>
              <Table.Row>
                <Table.Cell> </Table.Cell>
                <Table.Cell>.vsdx</Table.Cell>
                <Table.Cell>application/vnd.ms-visio.drawing</Table.Cell>
              </Table.Row>
              <Table.Row>
                <Table.Cell> </Table.Cell>
                <Table.Cell>.xlam</Table.Cell>
                <Table.Cell>application/vnd.ms-excel.addin.macroEnabled.12</Table.Cell>
              </Table.Row>
              <Table.Row>
                <Table.Cell>MS Excel 97</Table.Cell>
                <Table.Cell>.xls</Table.Cell>
                <Table.Cell>application/vnd.ms-excel</Table.Cell>
              </Table.Row>
              <Table.Row>
                <Table.Cell>MS Excel 2007 Binary</Table.Cell>
                <Table.Cell>.xlsb</Table.Cell>
                <Table.Cell>application/vnd.ms-excel.sheet.binary.macroEnabled.12</Table.Cell>
              </Table.Row>
              <Table.Row>
                <Table.Cell>MS Excel 97 (with macros)</Table.Cell>
                <Table.Cell>.xlsm</Table.Cell>
                <Table.Cell>application/vnd.ms-excel.sheet.macroEnabled.12</Table.Cell>
              </Table.Row>
              <Table.Row>
                <Table.Cell> </Table.Cell>
                <Table.Cell>.xlsx</Table.Cell>
                <Table.Cell>application/vnd.openxmlformats-officedocument.spreadsheetml.sheet</Table.Cell>
              </Table.Row>
              <Table.Row>
                <Table.Cell> </Table.Cell>
                <Table.Cell>.xltm</Table.Cell>
                <Table.Cell>application/vnd.ms-excel.template.macroEnabled.12</Table.Cell>
              </Table.Row>
              <Table.Row>
                <Table.Cell> </Table.Cell>
                <Table.Cell>.xltx</Table.Cell>
                <Table.Cell>application/vnd.openxmlformats-officedocument.spreadsheetml.template</Table.Cell>
              </Table.Row>
            </Table.Body>
          </Table>
          <p>
            The following example shows the text extraction and report options being enabled ensuring that only the report with document text will be returned by the API.
          </p>
          <SyntaxHighlighter language='json' style={SyntaxStyle}>
            {
              '{\n' +
              '  "mime-type": "application/octet-stream",\n' +
              '  "options": {\n' +
              '    {"conversion": { "methods": { "enabled": ["text-extraction", "report"] }}}\n' +
              '  }\n' +
              '}'
            }
          </SyntaxHighlighter>

          <Header as='h3'>Conversion failure</Header>
          <p>
            If for any reason the conversion could not be handled, either due to an unsupported conversion or a failiure in the
            actual conversion process. The error will be reported back and the file will not be transformed. All possible error
            messages are listed in the <Link to='/api/errors'>Error Glossary</Link>.
          </p>

          <Header as='h3'>Conversion limitations</Header>
          <p>
            Conversion is handled by third party tools, so any limitations of those tools also apply to our service.
            In addition on the S3 and Async API, conversion can limit the size of file that can be processed due to the extra space required by the conversion tools.
          </p>

        </Container>
      </Layout>
    )
  }
}
